import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { usePageMeta } from "@/hooks/usePageMeta";

const FORM_URL = "https://readdy.ai/api/form/dbb1cbuctmbk4sp5c9tg";

const aboutHero =
  "https://readdy.ai/api/search-image?query=Dark%20elegant%20reading%20room%20with%20burgundy%20velvet%20curtains%2C%20warm%20amber%20table%20lamp%2C%20dark%20wooden%20shelves%2C%20cinematic%20editorial%20interior%2C%20quiet%20atmosphere%2C%20no%20people%2C%20high%20detail&width=1600&height=600&seq=adult-about-hero&orientation=landscape";

const services = [
  {
    icon: "ri-play-circle-line",
    title: "视频内容",
    desc: "按剧情、情侣、唯美、欧美、日韩与独立制片分类整理，标注时长与清晰度，方便快速筛选。",
    to: "/video-category",
  },
  {
    icon: "ri-image-line",
    title: "写真图集",
    desc: "以光影、质感与留白为主的图集，支持大图缩放与前后切换，按主题分类浏览。",
    to: "/image-category",
  },
  {
    icon: "ri-newspaper-line",
    title: "行业资讯",
    desc: "关注隐私保护、内容分级与观看健康，提供克制的观察与实用建议。",
    to: "/news-category",
  },
  {
    icon: "ri-chat-3-line",
    title: "社区交流",
    desc: "聊作品，也聊观看这件事本身。话题讨论保持礼貌与克制，不鼓励暴露个人信息。",
    to: "/community",
  },
];

const principles = [
  {
    icon: "ri-shield-user-line",
    title: "适龄提示",
    desc: "首次访问需要确认已满 18 岁，确认状态只保存在你的设备上，不会上传。",
  },
  {
    icon: "ri-lock-line",
    title: "隐私最小化",
    desc: "本站不要求注册、不收集观看记录。收藏只写入浏览器本地存储，清理即消失。",
  },
  {
    icon: "ri-error-warning-line",
    title: "演示内容声明",
    desc: "所有视频、图集、资讯与社区内容均为演示素材，播放区域不含真实流媒体接入。",
  },
  {
    icon: "ri-file-text-line",
    title: "内容准则",
    desc: "不提供涉及未成年人的内容，不鼓励未经同意的传播，违规反馈会被优先处理。",
  },
];

const stats = [
  { value: "4", label: "内容板块" },
  { value: "1,280+", label: "演示条目" },
  { value: "2021", label: "项目起步年份" },
  { value: "18+", label: "访问年龄限制" },
];

export default function AboutPage() {
  usePageMeta({
    title: "关于我们 - 成人网",
    description:
      "了解成人网的定位与服务范围：视频、图片、资讯与社区四条内容线索，以及我们在适龄提示、隐私保护与内容准则上的做法。",
    keywords: "关于成人网,成人内容平台,隐私保护,内容准则,成人社区",
  });

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const honeypot = String(formData.get("website_alt") ?? "").trim();

    if (honeypot) {
      setStatus("success");
      setSuccessMessage("反馈已提交，感谢你的建议。");
      form.reset();
      return;
    }

    formData.delete("website_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === "string" && value.trim() !== "") {
        body.append(key, value);
      }
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === "OK") {
        setStatus("success");
        setSuccessMessage(serverMessage || "反馈已提交，感谢你的建议。");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMessage || "提交失败，请稍后再试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未完成，请检查网络后重试。");
    }
  };

  return (
    <div className="pb-4">
      <div className="container-page pt-7">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
      </div>

      <section className="relative h-[300px] w-full overflow-hidden md:h-[360px]">
        <img
          src={aboutHero}
          alt="成人网品牌介绍配图，暖光下的深色阅读空间"
          title="关于成人网 - 成人娱乐内容与资讯平台"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-950/94 via-background-950/70 to-background-950/30"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-transparent to-transparent"></div>
        <div className="container-page relative flex h-full flex-col justify-center">
          <span className="text-[11px] uppercase tracking-[0.28em] text-accent-400">
            About Adult Net
          </span>
          <h1 className="mt-3 font-heading text-[30px] text-foreground-950 md:text-[42px]">
            关于成人网
          </h1>
          <p className="mt-3 max-w-xl text-[13px] leading-relaxed text-foreground-600 md:text-sm">
            我们把成人内容整理成四条清晰的线索：视频、图集、资讯与社区。
            不追逐夸张的标题，也不做打扰式的推荐，只希望你在需要的时候，能安静地找到想看的东西。
          </p>
        </div>
      </section>

      <div className="container-page mt-10">
        <section className="grid grid-cols-1 gap-8 lg:grid-cols-[1.4fr_1fr] lg:items-center">
          <div>
            <h2 className="font-heading text-2xl text-foreground-950">网站定位</h2>
            <div className="mt-4 space-y-3.5 text-[13.5px] leading-[1.95] text-foreground-600">
              <p>
                成人网是一个面向成年观众的综合型内容门户。我们相信，成年内容同样需要体面的排版、
                清晰的分区和基本的隐私尊重——而不是密集的弹窗与诱导点击。
              </p>
              <p>
                因此，站内所有内容都带有明确的分类与元信息（时长、清晰度、上传时间、摄影师、主题），
                筛选与排序逻辑也尽量直观。你可以按心情找内容，而不必被算法牵着走。
              </p>
              <p>
                需要说明的是，当前站点是一个完整的界面与交互演示：视频播放、评论、话题发布与回复
                均为前端演示，不包含真实播放权限，也不会把内容写入服务器。
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {stats.map((item) => (
              <div
                key={item.label}
                className="rounded-lg border border-background-200 bg-background-100 px-4 py-5 text-center"
              >
                <p className="font-heading text-xl text-accent-500 md:text-2xl">{item.value}</p>
                <p className="mt-1.5 text-[11px] text-foreground-500">{item.label}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12">
          <div className="mb-5">
            <span className="text-[11px] uppercase tracking-[0.28em] text-accent-500">Services</span>
            <h2 className="mt-2 font-heading text-2xl text-foreground-950">服务范围</h2>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {services.map((item) => (
              <Link
                key={item.title}
                to={item.to}
                className="group rounded-lg border border-background-200 bg-background-100 p-5 transition-colors hover:border-accent-500/50"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500/15 text-lg text-primary-300 transition-colors group-hover:bg-primary-500/25">
                  <i className={item.icon}></i>
                </span>
                <h3 className="mt-4 text-sm font-medium text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-[11.5px] leading-relaxed text-foreground-500">
                  {item.desc}
                </p>
                <span className="mt-3 inline-flex items-center gap-1 text-[11px] text-accent-600">
                  前往浏览
                  <i className="ri-arrow-right-line"></i>
                </span>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-12">
          <div className="mb-5">
            <span className="text-[11px] uppercase tracking-[0.28em] text-accent-500">
              Principles
            </span>
            <h2 className="mt-2 font-heading text-2xl text-foreground-950">内容与隐私准则</h2>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {principles.map((item) => (
              <div
                key={item.title}
                className="flex gap-4 rounded-lg border border-background-200 bg-background-100 p-4"
              >
                <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-200 text-accent-500">
                  <i className={item.icon}></i>
                </span>
                <div>
                  <h3 className="text-sm text-foreground-950">{item.title}</h3>
                  <p className="mt-1.5 text-[11.5px] leading-relaxed text-foreground-500">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1.25fr]">
          <div>
            <span className="text-[11px] uppercase tracking-[0.28em] text-accent-500">Contact</span>
            <h2 className="mt-2 font-heading text-2xl text-foreground-950">联系我们</h2>
            <p className="mt-3 text-[13px] leading-relaxed text-foreground-500">
              内容建议、功能问题、违规举报或合作咨询，都可以通过下面的表单提交。
              我们会在一到两个工作日内查看，但无法保证每一条都得到单独回复。
            </p>

            <div className="mt-6 space-y-3">
              <a
                href="mailto:hello@chengrenwang.demo"
                className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3.5 transition-colors hover:border-accent-500/50"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-200 text-accent-500">
                  <i className="ri-mail-line"></i>
                </span>
                <span className="min-w-0">
                  <span className="block text-[11px] text-foreground-500">邮箱（演示地址）</span>
                  <span className="block truncate text-xs text-foreground-900">
                    hello@chengrenwang.demo
                  </span>
                </span>
              </a>
              <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3.5">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-200 text-accent-500">
                  <i className="ri-time-line"></i>
                </span>
                <span>
                  <span className="block text-[11px] text-foreground-500">响应时间</span>
                  <span className="block text-xs text-foreground-900">
                    工作日 10:00 – 19:00
                  </span>
                </span>
              </div>
              <div className="rounded-lg border border-primary-800/60 bg-primary-950/40 px-4 py-3.5">
                <p className="text-[11px] leading-relaxed text-foreground-500">
                  若涉及未成年人相关内容或明显的违法行为，请在表单中选择“举报违规”，
                  并尽量提供页面链接，我们会优先处理。
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-background-200 bg-background-100 p-5 md:p-6">
            <h2 className="text-sm font-medium text-foreground-950">意见反馈表单</h2>
            <p className="mt-1.5 text-[11.5px] text-foreground-500">
              标记为必填的字段需要填写。反馈内容最多 500 字。
            </p>

            {status === "success" ? (
              <div className="mt-4 flex items-start gap-2 rounded-md border border-accent-500/40 bg-accent-500/10 px-3.5 py-3 text-[12px] leading-relaxed text-accent-500">
                <i className="ri-checkbox-circle-line mt-0.5"></i>
                <span>{successMessage}</span>
              </div>
            ) : null}

            {status === "error" ? (
              <div className="mt-4 flex items-start gap-2 rounded-md border border-primary-600/50 bg-primary-950/50 px-3.5 py-3 text-[12px] leading-relaxed text-foreground-800">
                <i className="ri-error-warning-line mt-0.5"></i>
                <span>{formError}</span>
              </div>
            ) : null}

            <form
              id="about-feedback-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-5 space-y-4"
              autoComplete="off"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="min-w-0">
                  <label
                    htmlFor="feedback-nickname"
                    className="mb-1.5 block text-[11px] text-foreground-500"
                  >
                    昵称
                  </label>
                  <input
                    id="feedback-nickname"
                    name="nickname"
                    type="text"
                    maxLength={40}
                    placeholder="怎么称呼你"
                    className="box-border w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-600 focus:border-accent-500 focus:outline-none"
                  />
                </div>
                <div className="min-w-0">
                  <label
                    htmlFor="feedback-email"
                    className="mb-1.5 block text-[11px] text-foreground-500"
                  >
                    邮箱 <span className="text-primary-400">*</span>
                  </label>
                  <input
                    id="feedback-email"
                    name="email"
                    type="email"
                    required
                    maxLength={120}
                    placeholder="用于接收回复"
                    className="box-border w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-600 focus:border-accent-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="min-w-0">
                <label
                  htmlFor="feedback-type"
                  className="mb-1.5 block text-[11px] text-foreground-500"
                >
                  反馈类型 <span className="text-primary-400">*</span>
                </label>
                <select
                  id="feedback-type"
                  name="feedback_type"
                  required
                  defaultValue="内容建议"
                  className="box-border w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:border-accent-500 focus:outline-none"
                >
                  <option value="内容建议">内容建议</option>
                  <option value="功能问题">功能问题</option>
                  <option value="举报违规">举报违规</option>
                  <option value="合作咨询">合作咨询</option>
                </select>
              </div>

              <div className="min-w-0">
                <label
                  htmlFor="feedback-message"
                  className="mb-1.5 block text-[11px] text-foreground-500"
                >
                  反馈内容 <span className="text-primary-400">*</span>
                </label>
                <textarea
                  id="feedback-message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="请描述你遇到的问题或建议（最多 500 字）"
                  className="box-border w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-600 focus:border-accent-500 focus:outline-none"
                ></textarea>
              </div>

              <div className="field-reserved-note" aria-hidden="true">
                <label htmlFor="feedback-website-alt">请勿填写此栏</label>
                <input
                  id="feedback-website-alt"
                  name="website_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-[10.5px] leading-relaxed text-foreground-600">
                  提交即表示你同意我们仅将信息用于本次反馈处理。
                </p>
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="flex items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-send-plane-line"></i>
                  {status === "submitting" ? "提交中…" : "提交反馈"}
                </button>
              </div>
            </form>
          </div>
        </section>

        <section className="mt-12 rounded-xl border border-background-200 bg-background-100 px-6 py-7">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-[1.6fr_1fr] md:items-center">
            <div>
              <h2 className="font-heading text-xl text-foreground-950">关于演示范围的说明</h2>
              <p className="mt-2.5 text-[12.5px] leading-relaxed text-foreground-500">
                本项目的所有内容、作者、摄影师、话题与回复均为演示数据；视频区域为静态界面，
                不接入真实流媒体；社区发帖与回复不会上传服务器；收藏信息仅保存在当前设备的浏览器中。
                如后续需要真实的账号体系、持久化收藏或评论，可以再接入后端服务。
              </p>
            </div>
            <div className="flex flex-wrap gap-2 md:justify-end">
              <Link
                to="/video-category"
                className="rounded-md border border-background-300 px-4 py-2 text-xs text-foreground-600 transition-colors hover:border-accent-500 hover:text-accent-500"
              >
                浏览视频
              </Link>
              <Link
                to="/community"
                className="rounded-md border border-background-300 px-4 py-2 text-xs text-foreground-600 transition-colors hover:border-accent-500 hover:text-accent-500"
              >
                参与社区
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}